import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import {
  ArrowUpRight, ArrowRight, Award, ShieldCheck, Cpu, Server, Database,
  Headphones, Bot, Wrench, GraduationCap, Globe, Youtube, Check,
  TrendingUp, Quote, Sparkles, Rocket, Video, Brain, UserPlus, ExternalLink,
} from "lucide-react";
import { LMS_URL } from "../lib/config";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useState } from "react";

const IMG = {
  founder: "https://static.prod-images.emergentagent.com/jobs/370b85a0-67e2-477c-8098-5e07f3dff1b6/images/dddb86808f320c93d48318f677bfa2e31f9b5dcc83772414dfad3f6358393f9e.jpeg",
  server: "https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwc2VydmVyJTIwcmFjayUyMGdsb3dpbmd8ZW58MHx8fHwxNzg2MTY5MDU4fDA&ixlib=rb-4.1.0&q=85",
  nodes: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG5ldHdvcmslMjBub2Rlc3xlbnwwfHx8fDE3ODYxOTk5NjR8MA&ixlib=rb-4.1.0&q=85",
  office: "https://images.unsplash.com/photo-1574848296471-28f79a036f79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwb2ZmaWNlfGVufDB8fHx8MTc4NjE5OTk2NHww&ixlib=rb-4.1.0&q=85",
};

const ease = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const MaskLine = ({ children, delay = 0 }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const SectionLabel = ({ children, num }) => (
  <div className="flex items-center gap-3 mb-6">
    {num && <span className="font-mono text-[11px] text-cyan-400">{num}</span>}
    <span className="h-px w-8 bg-cyan-400/60" />
    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">{children}</span>
  </div>
);

/* ---------------------------------- HERO ---------------------------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden pt-24 pb-16">
      <motion.div style={{ y, scale }} className="absolute top-0 right-0 w-full lg:w-[52%] h-full">
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10" />
        <img src={IMG.nodes} alt="AI network" className="w-full h-full object-cover grayscale opacity-70" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-10 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-400">
            AiSho — AI &amp; Operations Consulting
          </span>
        </motion.div>

        <h1 className="font-serif text-[13vw] leading-[0.92] md:text-[9vw] lg:text-[7.5vw] tracking-tight text-[#F3F3F1] max-w-[16ch]">
          <MaskLine delay={0.25}>Driving Enterprise</MaskLine>
          <MaskLine delay={0.38}>Efficiency &amp;</MaskLine>
          <MaskLine delay={0.51}>
            Next-Gen <span className="italic text-cyan-400">Automation.</span>
          </MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease }}
          className="mt-10 flex flex-col md:flex-row md:items-end gap-8 md:justify-between"
        >
          <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-md">
            Bridging nearly 20 years of operational leadership with proprietary AI technology to scale your business.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-button"
              className="group flex items-center gap-2 bg-cyan-400 hover:bg-white text-black px-7 py-4 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300"
            >
              Start a Project
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#services"
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors border-b border-white/20 pb-1"
            >
              Explore Services
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

/* -------------------------------- MARQUEE --------------------------------- */
const marqueeItems = [
  "Lean Six Sigma", "Google Ads Certified", "Google Analytics", "Europace Call Center Award",
  "LLM Server Architecture", "Enterprise BPO", "Process Automation", "AI Moderation",
];
const TickerBand = () => (
  <div className="py-6 border-y border-white/10 bg-[#0C0C0C]">
    <Marquee speed={40} gradient gradientColor="#0C0C0C" gradientWidth={120}>
      {marqueeItems.concat(marqueeItems).map((t, i) => (
        <span key={i} className="mx-8 font-serif text-2xl md:text-3xl text-white/40 flex items-center gap-8">
          {t} <span className="text-cyan-400/50 text-lg">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

/* --------------------------------- ABOUT ---------------------------------- */
const certs = [
  { icon: ShieldCheck, label: "Lean Six Sigma (Green Belt)" },
  { icon: Check, label: "Google Ads Certified" },
  { icon: Cpu, label: "AI-Powered Development" },
  { icon: Award, label: "ECCCSA 2023 — Best BPO Partnership" },
];
const About = () => (
  <section id="about" className="relative py-32 md:py-40 max-w-[1400px] mx-auto px-6 md:px-10">
    <SectionLabel num="01">The Architect</SectionLabel>
    <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
      <Reveal className="lg:col-span-5">
        <div className="relative">
          <div className="absolute -inset-3 border border-white/10 rounded-sm" />
          <div className="relative overflow-hidden rounded-sm aspect-[4/5]">
            <img src={IMG.founder} alt="Jonathan Deroy" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5">
              <p className="font-serif text-2xl text-white">Jonathan Deroy</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mt-1">Founder &amp; Lead Architect</p>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="lg:col-span-7">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F3F3F1]">
            Two decades of operational mastery, <span className="italic text-white/50">engineered into intelligence.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 text-base md:text-lg text-white/60 leading-relaxed max-w-2xl">
            Jonathan Deroy has spent nearly 20 years across Business Process Outsourcing, operations, training, and quality
            management in global verticals — from front-line customer excellence to leading Business Quality Management
            at Foundever and directing training — and, for several accounts, quality management — across nine global and
            EMEA accounts as a Training Manager at Teleperformance.
            His flagship campaign ranked #1 globally in process execution for two consecutive years, and the team he
            led earned the <span className="text-white/85">ECCCSA 2023 award for Best BPO Partnership</span>.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 text-base md:text-lg text-white/60 leading-relaxed max-w-2xl">
            He's also a hands-on AI practitioner — designing bespoke AI tools, building and managing generative AI agents
            (selected for Teleperformance's global Copilot pilot), and keeping AiSho <span className="text-cyan-400">ahead of the curve</span> on
            worldwide AI developments through our own internal AI LMS. Multilingual across English, French, Dutch, and German.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {certs.map((c) => (
              <div key={c.label} className="bg-[#0C0C0C] p-6 flex items-center gap-4 hover:bg-[#121212] transition-colors">
                <c.icon size={20} strokeWidth={1.5} className="text-cyan-400 shrink-0" />
                <span className="text-sm text-white/80">{c.label}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-12 flex flex-wrap gap-12">
            {[
              { n: "20", l: "Years Experience" },
              { n: "#1", l: "Global Process Ranking" },
              { n: "ECCCSA", l: "2023 Award Winner" },
            ].map((s) => (
              <div key={s.l}>
                <p className="font-serif text-5xl text-white">{s.n}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">{s.l}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ------------------------------ TECHNOLOGY -------------------------------- */
const techFeatures = [
  { icon: GraduationCap, title: "Internal AI LMS", body: "Our own learning platform continuously trains the team on worldwide AI developments — how we stay ahead of the curve and learn fast." },
  { icon: Cpu, title: "On-Prem Proprietary LLM", body: "A large language model we built and run for ourselves — hosted internally on a secure, dedicated local server. Not for sale; it's our engine." },
  { icon: Bot, title: "AI Agent Engineering", body: "We design, build, and manage AI agents for clients — making them measurably more effective, reliable, and cost-efficient." },
  { icon: Server, title: "Secure Local Infrastructure", body: "Everything runs on a dedicated on-premise server with full data sovereignty, low latency, and zero third-party exposure." },
];
const Technology = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  return (
    <section id="technology" ref={ref} className="relative py-32 md:py-40 bg-[#0C0C0C] border-y border-white/10 overflow-hidden">
      <motion.img style={{ y }} src={IMG.server} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover grayscale opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0C0C0C] via-[#0C0C0C]/90 to-[#0C0C0C]/40" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionLabel num="02">Our Technology Edge</SectionLabel>
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F3F3F1] max-w-3xl">
            We don't sell the model — <span className="italic text-cyan-400">we run our own.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base md:text-lg text-white/60 max-w-xl">
            AiSho operates a proprietary LLM and internal AI LMS on a secure, dedicated local server — the engine we use
            to stay ahead of the curve. For clients, we turn that edge into practical outcomes: building and managing
            AI agents that make your operations dramatically more effective.
          </p>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden max-w-4xl">
          {techFeatures.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="bg-[#050505] p-8 md:p-10 h-full group hover:bg-[#101010] transition-colors duration-300">
                <f.icon size={26} strokeWidth={1.25} className="text-cyan-400 mb-6" />
                <h3 className="font-serif text-2xl text-white mb-3">{f.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------------------- SERVICES -------------------------------- */
const services = [
  { icon: Headphones, title: "BPO & Operations Optimization", body: "Streamline outsourced operations with award-winning quality frameworks and Lean Six Sigma rigor." },
  { icon: Bot, title: "AI Agent Development & Management", body: "We design, build, and manage AI agents for your workflows — and keep making them more effective over time." },
  { icon: Youtube, title: "AI Moderation & Clipper Tools", body: "AiSho-trained moderators who bring our mindset — pairing the right AI tools with analytics dashboards to moderate efficiently and grow streamers' channels." },
  { icon: Wrench, title: "Custom Tools & Automation", body: "Bespoke internal tools and end-to-end automations that eliminate manual, repetitive work." },
  { icon: Globe, title: "Small Business Web Design", body: "Fast, elegant, conversion-focused websites for small businesses ready to scale." },
  { icon: Cpu, title: "Media & Digital Strategy", body: "Content and digital growth strategy, integrated with the AiSho Explains YouTube channel." },
];
const Services = () => (
  <section id="services" className="py-32 md:py-40 max-w-[1400px] mx-auto px-6 md:px-10">
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
      <div>
        <SectionLabel num="03">Capabilities</SectionLabel>
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F3F3F1] max-w-2xl">
            A full-stack consultancy for the AI era.
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-6 text-base md:text-lg text-white/60 leading-relaxed max-w-2xl">
            We're consultants — with a plus. After evaluating your workload, we recommend the right tools to use,
            provide a consultant to run and follow up on them with you, or have our vibe-coding experts custom-build
            whatever makes you more effective — resolving issues, or preventing them before they start.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <a href="#contact" className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-400 flex items-center gap-2 hover:gap-3 transition-all">
          Discuss your needs <ArrowRight size={14} />
        </a>
      </Reveal>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
      {services.map((s, i) => (
        <Reveal key={s.title} delay={(i % 3) * 0.08}>
          <div
            data-testid={`service-card-${i}`}
            className="group relative bg-[#0C0C0C] p-10 h-full min-h-[280px] flex flex-col justify-between hover:bg-[#101010] transition-colors duration-300"
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div>
              <span className="font-mono text-[11px] text-white/25">0{i + 1}</span>
              <s.icon size={28} strokeWidth={1.25} className="text-cyan-400 mt-6 mb-6 transition-transform duration-300 group-hover:-translate-y-1" />
              <h3 className="font-serif text-2xl text-white leading-snug">{s.title}</h3>
            </div>
            <p className="text-sm text-white/55 leading-relaxed mt-4">{s.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ----------------------------- CASE STUDIES ------------------------------- */
const impact = [
  { metric: "#1", label: "Globally in process execution", body: "Led a flagship BPO campaign to the top global ranking in internal process execution — two consecutive years." },
  { metric: "ECCCSA", label: "2023 — Best BPO Partnership", body: "A recognition earned with the team Jonathan led — the European Contact Centre & Customer Service Award for Best BPO Partnership." },
  { metric: "9", label: "Global & EMEA accounts", body: "Directed training across nine global and EMEA accounts — serving as quality manager on several — leading a dedicated team of trainers, QAs and coordinators." },
  { metric: "QaaS", label: "Framework launched from zero", body: "Architected and deployed a Quality-as-a-Service framework from scratch across all lines of business." },
];
const testimonials = [
  { quote: "Jonathan rebuilt our quality and training function from the ground up. The consistency gains were immediate and measurable.", who: "Global Operations Director", org: "Enterprise BPO Client" },
  { quote: "He doesn't just talk about AI — he ships the tools. Our agents got faster, sharper, and far more reliable.", who: "Program Lead", org: "AI Copilot Pilot" },
];
const CaseStudies = () => (
  <section id="impact" className="py-32 md:py-40 max-w-[1400px] mx-auto px-6 md:px-10">
    <SectionLabel num="04">Proven Impact</SectionLabel>
    <Reveal>
      <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F3F3F1] max-w-3xl">
        A track record, <span className="italic text-cyan-400">not a pitch.</span>
      </h2>
    </Reveal>
    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
      {impact.map((c, i) => (
        <Reveal key={c.label} delay={(i % 4) * 0.08}>
          <div className="bg-[#0C0C0C] p-8 h-full hover:bg-[#101010] transition-colors duration-300">
            <TrendingUp size={20} strokeWidth={1.25} className="text-cyan-400 mb-6" />
            <p className="font-serif text-5xl text-white leading-none">{c.metric}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 mt-3">{c.label}</p>
            <p className="text-sm text-white/55 leading-relaxed mt-5">{c.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <div className="mt-8 grid md:grid-cols-2 gap-8">
      {testimonials.map((t, i) => (
        <Reveal key={i} delay={i * 0.1}>
          <div className="border border-white/10 rounded-sm p-8 md:p-10 bg-[#0C0C0C] h-full">
            <Quote size={26} className="text-cyan-400/70 mb-5" />
            <p className="font-serif text-2xl md:text-[1.65rem] leading-snug text-white/90">"{t.quote}"</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-6">{t.who} · {t.org}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ------------------------------- MANIFESTO -------------------------------- */
const chapters = [
  { n: "01", t: "Process before platform", b: "Technology only compounds value when it sits on operational discipline. We fix the workflow, then we automate it." },
  { n: "02", t: "Owned, not rented", b: "Our LMS and LLM run on dedicated local infrastructure — your intelligence stays yours, sovereign and secure." },
  { n: "03", t: "Human judgment, machine scale", b: "Twenty years of operational leadership, encoded into systems that scale without losing the human standard." },
];
const Manifesto = () => (
  <section id="manifesto" className="py-32 md:py-40 bg-[#0C0C0C] border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionLabel num="05">The Approach</SectionLabel>
      <div className="mt-12 space-y-px">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.05}>
            <div className="group grid md:grid-cols-12 gap-6 md:gap-10 items-baseline py-12 border-t border-white/10 hover:border-cyan-400/40 transition-colors duration-500">
              <span className="md:col-span-2 font-serif text-6xl md:text-7xl text-white/15 group-hover:text-cyan-400/70 transition-colors duration-500">{c.n}</span>
              <h3 className="md:col-span-4 font-serif text-3xl md:text-4xl text-white leading-tight">{c.t}</h3>
              <p className="md:col-span-6 text-base text-white/55 leading-relaxed">{c.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------ LEARNING HUB ------------------------------ */
const courseTopics = [
  { icon: Rocket, t: "Vibe Coding" },
  { icon: Video, t: "Video Editing & Generation" },
  { icon: Sparkles, t: "Stable Diffusion" },
  { icon: Brain, t: "Generative AI" },
  { icon: Cpu, t: "Build Your Own LLM" },
  { icon: GraduationCap, t: "Build an LMS" },
];
const coursePreviews = [
  { icon: Rocket, title: "Vibe Coding 101", blurb: "Ship real apps with AI as your pair-programmer — from idea to deployed product.", level: "Beginner", length: "6 modules" },
  { icon: Sparkles, title: "Stable Diffusion Studio", blurb: "Master prompt craft, models, and workflows to generate stunning visuals on demand.", level: "Intermediate", length: "8 modules" },
  { icon: Cpu, title: "Build Your Own LLM", blurb: "Understand, fine-tune, and deploy your own language model — the AiSho way.", level: "Advanced", length: "10 modules" },
];
const Learning = () => {
  const [selected, setSelected] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const toggle = (t) => setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const join = async (e) => {
    e.preventDefault();
    if (!selected.length) return toast.error("Pick at least one topic you'd like us to launch.");
    setLoading(true);
    try {
      await api.post("/waitlist", { email, topics: selected });
      toast.success("You're on the waitlist! We'll email you the moment it launches.");
      setSelected([]);
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="learning" className="py-32 md:py-40 bg-[#0C0C0C] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <SectionLabel num="06">Learning Hub — Coming Soon</SectionLabel>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F3F3F1]">
                Create an account today. <span className="italic text-cyan-400">Learn with us tomorrow.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base md:text-lg text-white/60 leading-relaxed">
                Anyone can sign up free to engage with us and follow up on projects. Our internal AI LMS is migrating to
                this platform soon — and depending on your profile, you'll be able to take and buy courses where we teach
                and coach, one-on-one or online, on anything AI.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="flex flex-col gap-3">
              <a
                href="/register"
                data-testid="learning-signup-cta"
                className="group flex items-center gap-2 bg-cyan-400 hover:bg-white text-black px-7 py-4 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 whitespace-nowrap"
              >
                <UserPlus size={16} /> Create Free Account
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              {LMS_URL ? (
                <a
                  href={LMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="learning-lms-launch"
                  className="group flex items-center justify-center gap-2 border border-cyan-400/50 hover:border-cyan-400 hover:bg-cyan-400/10 text-cyan-400 px-7 py-4 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 whitespace-nowrap"
                >
                  <ExternalLink size={15} /> Launch LMS
                </a>
              ) : (
                <span
                  data-testid="learning-lms-soon"
                  className="flex items-center justify-center gap-2 border border-white/15 text-white/40 px-7 py-4 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] cursor-not-allowed whitespace-nowrap"
                >
                  <ExternalLink size={15} /> LMS — Launching Soon
                </span>
              )}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-6">First courses in production</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {coursePreviews.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <div data-testid={`course-preview-${i}`} className="group relative bg-[#050505] border border-white/10 rounded-sm p-7 h-full overflow-hidden hover:border-cyan-400/40 transition-colors duration-300">
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-cyan-400/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-6">
                    <c.icon size={26} strokeWidth={1.25} className="text-cyan-400" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 border border-white/15 px-2 py-1 rounded-full">Coming Soon</span>
                  </div>
                  <h3 className="font-serif text-2xl text-white mb-3">{c.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed min-h-[60px]">{c.blurb}</p>
                  <div className="mt-6 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">
                    <span>{c.level}</span><span className="text-white/20">·</span><span>{c.length}</span>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">
            Which topics should we launch first? Tap to pick.
          </p>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
          {courseTopics.map((c, i) => {
            const on = selected.includes(c.t);
            return (
              <Reveal key={c.t} delay={(i % 6) * 0.06}>
                <button
                  type="button"
                  onClick={() => toggle(c.t)}
                  data-testid={`waitlist-topic-${i}`}
                  className={`w-full text-left p-6 h-full flex flex-col gap-4 transition-colors duration-300 ${
                    on ? "bg-cyan-400/10 ring-1 ring-inset ring-cyan-400/60" : "bg-[#050505] hover:bg-[#101010]"
                  }`}
                >
                  <c.icon size={22} strokeWidth={1.25} className={on ? "text-cyan-400" : "text-cyan-400/70"} />
                  <span className={`text-sm leading-snug ${on ? "text-white" : "text-white/80"}`}>{c.t}</span>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={join} data-testid="waitlist-form" className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
            <Input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="waitlist-email"
              className="bg-[#050505] border-white/15 h-12 rounded-full px-5 focus-visible:ring-cyan-400 placeholder:text-white/25"
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="waitlist-submit"
              className="bg-cyan-400 hover:bg-white text-black rounded-full h-12 px-7 font-mono text-[11px] uppercase tracking-[0.2em] shrink-0"
            >
              {loading ? "Joining…" : "Join the Waitlist"}
            </Button>
          </form>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/35">
            + AI tools, workflow automation, and more — taught by practitioners who stay ahead of the curve.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

/* --------------------------------- MEDIA ---------------------------------- */
const Media = () => (
  <section className="py-32 md:py-40 max-w-[1400px] mx-auto px-6 md:px-10">
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <Reveal>
        <SectionLabel num="07">Media &amp; Digital Strategy</SectionLabel>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F3F3F1]">
          Watch how we <span className="italic text-cyan-400">explain</span> the future of operations.
        </h2>
        <p className="mt-6 text-base md:text-lg text-white/60 leading-relaxed max-w-lg">
          Our digital strategy is built in the open. The AiSho Explains channel breaks down AI, automation, and
          operational excellence — the same thinking we bring to every engagement.
        </p>
        <a
          href="https://youtube.com/@aishoexplains?si=rYthtNeLYRCvwtZW"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="youtube-link"
          className="mt-8 inline-flex items-center gap-2.5 bg-[#0C0C0C] border border-white/15 hover:border-cyan-400 text-white px-6 py-3.5 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
        >
          <Youtube size={16} className="text-cyan-400" /> AiSho Explains
        </a>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="relative overflow-hidden rounded-sm border border-white/10 aspect-video group">
          <img src={IMG.office} alt="AiSho media" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <a href="https://youtube.com/@aishoexplains?si=rYthtNeLYRCvwtZW" target="_blank" rel="noopener noreferrer" className="h-20 w-20 rounded-full bg-cyan-400/90 text-black flex items-center justify-center hover:scale-110 transition-transform">
              <Youtube size={30} />
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------------------- CONTACT --------------------------------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Inquiry received. Jonathan's team will reach out shortly.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-32 md:py-40 bg-[#0C0C0C] border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <SectionLabel num="08">Start a Conversation</SectionLabel>
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F3F3F1]">
              Let's engineer your next stage of scale.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base text-white/55 leading-relaxed max-w-md">
              Tell us where you are and where you want to be. We'll map the operational and AI path to get there.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Name</Label>
              <Input required value={form.name} onChange={set("name")} data-testid="contact-name" className="bg-[#050505] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400" />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Email</Label>
              <Input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="bg-[#050505] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400" />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Company</Label>
              <Input value={form.company} onChange={set("company")} data-testid="contact-company" className="bg-[#050505] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400" />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Service of Interest</Label>
              <Input value={form.service} onChange={set("service")} data-testid="contact-service" placeholder="e.g. Automation, LMS, BPO" className="bg-[#050505] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400 placeholder:text-white/25" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Message</Label>
              <Textarea required rows={4} value={form.message} onChange={set("message")} data-testid="contact-message" className="bg-[#050505] border-white/15 rounded-sm focus-visible:ring-cyan-400 resize-none" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={loading} data-testid="contact-form-submit" className="w-full sm:w-auto bg-cyan-400 hover:bg-white text-black rounded-full h-12 px-8 font-mono text-[11px] uppercase tracking-[0.2em]">
                {loading ? "Sending…" : "Send Inquiry"}
                <ArrowUpRight size={16} className="ml-2" />
              </Button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-[#050505] border-t border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 flex flex-col md:flex-row justify-between gap-10">
      <div>
        <span className="font-serif text-3xl text-[#F3F3F1]">Ai<span className="text-cyan-400">.</span>Sho</span>
        <p className="mt-4 text-sm text-white/40 max-w-xs">Elite AI &amp; operations consulting. Enterprise efficiency, next-gen automation.</p>
      </div>
      <div className="flex gap-16">
        <div className="flex flex-col gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">Navigate</span>
          {["about", "technology", "services", "contact"].map((l) => (
            <a key={l} href={`#${l}`} className="text-sm text-white/60 hover:text-cyan-400 transition-colors capitalize">{l}</a>
          ))}
        </div>
        <div className="flex flex-col gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">Connect</span>
          <a href="https://youtube.com/@aishoexplains?si=rYthtNeLYRCvwtZW" target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 hover:text-cyan-400 transition-colors">YouTube</a>
          <a href="https://www.linkedin.com/in/jonathan-deroy-735812134/" target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 hover:text-cyan-400 transition-colors">LinkedIn</a>
          <a href="/login" className="text-sm text-white/60 hover:text-cyan-400 transition-colors">Client Portal</a>
        </div>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/25">© 2026 AiSho — Jonathan Deroy. All rights reserved.</p>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div className="relative bg-[#050505]">
      <Navbar />
      <Hero />
      <TickerBand />
      <About />
      <Technology />
      <Services />
      <CaseStudies />
      <Manifesto />
      <Learning />
      <Media />
      <Contact />
      <Footer />
    </div>
  );
}
